import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert, Grid,
  Switch, Tabs, Tab, FormControlLabel, Checkbox, MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import adminApi from "../api/adminApi";

// Banners and discount codes for the website (BackendNode routes/promotions.js, routes/discountCodes.js).
const LOCALES = [
  { code: "en", label: "English" },
  { code: "pt", label: "Português" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "pl", label: "Polski" },
];
const PLACEMENTS = [
  { code: "home", label: "Home page" },
  { code: "fleet", label: "Fleet page" },
];
const NAVY = "#09233f";
const ORANGE = "#f47a20";

const emptyContent = () =>
  Object.fromEntries(LOCALES.map((l) => [l.code, { eyebrow: "", title: "", highlight: "", cta: "" }]));
const dateInput = (d) => (d ? String(d).slice(0, 10) : "");
const errorText = (err, fallback) => err.response?.data?.message || fallback;

/** The banner as the website draws it (dark card, orange accent). */
const BannerPreview = ({ content }) => (
  <Box sx={{ position: "relative", overflow: "hidden", bgcolor: NAVY, color: "#fff", borderRadius: "24px", p: "22px 26px", maxWidth: 470 }}>
    <Box sx={{ position: "absolute", width: 150, height: 150, borderRadius: "50%", bgcolor: ORANGE, right: -85, bottom: -90 }} />
    <Typography sx={{ position: "relative", color: ORANGE, fontSize: 10, fontWeight: 800, letterSpacing: ".18em", textTransform: "uppercase", mb: 1 }}>
      {content.eyebrow || " "}
    </Typography>
    <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
      <Typography component="strong" sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-.03em" }}>
        {content.title} <span style={{ color: ORANGE }}>{content.highlight}</span>
      </Typography>
      {content.cta && (
        <Box sx={{ bgcolor: ORANGE, borderRadius: 999, px: 2, py: 1.25, fontSize: 10, fontWeight: 800, textTransform: "uppercase", whiteSpace: "nowrap" }}>
          {content.cta} →
        </Box>
      )}
    </Box>
  </Box>
);

const PromotionDialog = ({ open, onClose, promotion, onSaved }) => {
  const [form, setForm] = useState(null);
  const [locale, setLocale] = useState("en");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(
      promotion
        ? {
            name: promotion.name,
            active: promotion.active,
            placements: promotion.placements || [],
            startsAt: dateInput(promotion.startsAt),
            endsAt: dateInput(promotion.endsAt),
            order: promotion.order ?? 0,
            discountCode: promotion.discountCode || "",
            content: { ...emptyContent(), ...promotion.content },
          }
        : { name: "", active: false, placements: ["home", "fleet"], startsAt: "", endsAt: "", order: 0, discountCode: "", content: emptyContent() }
    );
    setLocale("en");
    setError("");
  }, [promotion, open]);

  if (!form) return null;
  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setText = (field) => (e) =>
    setForm((p) => ({ ...p, content: { ...p.content, [locale]: { ...p.content[locale], [field]: e.target.value } } }));
  const togglePlacement = (code) =>
    setForm((p) => ({
      ...p,
      placements: p.placements.includes(code) ? p.placements.filter((c) => c !== code) : [...p.placements, code],
    }));

  // Empty fields in another language show the English text on the site.
  const previewContent = Object.fromEntries(
    ["eyebrow", "title", "highlight", "cta"].map((f) => [f, form.content[locale][f] || form.content.en[f]])
  );

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (promotion?._id) await adminApi.put(`/promotions/${promotion._id}`, form);
      else await adminApi.post("/promotions", form);
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err, "Save failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{promotion?._id ? "Edit Banner" : "New Banner"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}><TextField label="Name (admin only)" fullWidth value={form.name} onChange={set("name")} /></Grid>
          <Grid item xs={6} md={3}><TextField label="Discount code (optional)" fullWidth value={form.discountCode} onChange={set("discountCode")} helperText="Shown to the customer" /></Grid>
          <Grid item xs={6} md={3}><TextField label="Order" type="number" fullWidth value={form.order} onChange={set("order")} helperText="Lower first" /></Grid>
          <Grid item xs={6} md={3}><TextField label="Starts" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.startsAt} onChange={set("startsAt")} /></Grid>
          <Grid item xs={6} md={3}><TextField label="Ends" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.endsAt} onChange={set("endsAt")} /></Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            {PLACEMENTS.map((p) => (
              <FormControlLabel key={p.code} label={p.label} control={<Checkbox checked={form.placements.includes(p.code)} onChange={() => togglePlacement(p.code)} />} />
            ))}
            <FormControlLabel label="On site" control={<Switch checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />} />
          </Grid>
        </Grid>

        <Tabs value={locale} onChange={(e, v) => setLocale(v)} variant="scrollable" sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}>
          {LOCALES.map((l) => <Tab key={l.code} value={l.code} label={l.code === "en" ? `${l.label} *` : l.label} />)}
        </Tabs>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}><TextField label="Small line (e.g. Why Not offer)" fullWidth value={form.content[locale].eyebrow} onChange={setText("eyebrow")} placeholder={locale === "en" ? "" : form.content.en.eyebrow} /></Grid>
          <Grid item xs={12} md={6}><TextField label="Button (e.g. Book now)" fullWidth value={form.content[locale].cta} onChange={setText("cta")} placeholder={locale === "en" ? "" : form.content.en.cta} /></Grid>
          <Grid item xs={12} md={6}><TextField label="Big text, white (e.g. WN)" fullWidth value={form.content[locale].title} onChange={setText("title")} placeholder={locale === "en" ? "" : form.content.en.title} /></Grid>
          <Grid item xs={12} md={6}><TextField label="Big text, orange (e.g. 15%)" fullWidth value={form.content[locale].highlight} onChange={setText("highlight")} placeholder={locale === "en" ? "" : form.content.en.highlight} /></Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
          Preview ({LOCALES.find((l) => l.code === locale).label}). Empty fields use the English text.
        </Typography>
        <BannerPreview content={previewContent} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ backgroundColor: "#1C2541" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EMPTY_CODE = { code: "", description: "", type: "percent", value: 10, minDays: 0, validFrom: "", validTo: "", maxUses: 0, active: true };

const CodeDialog = ({ open, onClose, code, onSaved }) => {
  const [form, setForm] = useState(EMPTY_CODE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(code ? { ...EMPTY_CODE, ...code, validFrom: dateInput(code.validFrom), validTo: dateInput(code.validTo) } : EMPTY_CODE);
    setError("");
  }, [code, open]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (extra = {}) => {
    setLoading(true);
    setError("");
    try {
      const body = { ...form, ...extra };
      if (code?._id) await adminApi.put(`/discount-codes/${code._id}`, body);
      else await adminApi.post("/discount-codes", body);
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err, "Save failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{code?._id ? "Edit Discount Code" : "New Discount Code"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField label="Code" fullWidth value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))} helperText="Letters, numbers and -" />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel label="Active" control={<Switch checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Type" fullWidth value={form.type} onChange={set("type")}>
              <MenuItem value="percent">Percentage off the rental</MenuItem>
              <MenuItem value="fixed">Euros off the rental</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label={form.type === "percent" ? "Percentage (1-90)" : "Amount (€)"} type="number" fullWidth value={form.value} onChange={set("value")} />
          </Grid>
          <Grid item xs={6}><TextField label="Valid from" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.validFrom} onChange={set("validFrom")} /></Grid>
          <Grid item xs={6}><TextField label="Valid until" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.validTo} onChange={set("validTo")} helperText="Last day the code can be used" /></Grid>
          <Grid item xs={6}><TextField label="Minimum rental days" type="number" fullWidth value={form.minDays} onChange={set("minDays")} helperText="0 = any length" /></Grid>
          <Grid item xs={6}><TextField label="Maximum uses" type="number" fullWidth value={form.maxUses} onChange={set("maxUses")} helperText={`0 = unlimited${code?._id ? ` · used ${code.uses}` : ""}`} /></Grid>
          <Grid item xs={12}><TextField label="Notes (admin only)" fullWidth value={form.description} onChange={set("description")} /></Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          The discount applies to the car rental only, not to extras, fees or the tourist tax. A use is counted when a booking with the code succeeds.
        </Typography>
      </DialogContent>
      <DialogActions>
        {code?._id && code.uses > 0 && <Button onClick={() => handleSubmit({ resetUses: true })} disabled={loading}>Reset uses</Button>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => handleSubmit()} disabled={loading} sx={{ backgroundColor: "#1C2541" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const period = (from, to) => (from || to ? `${formatDate(from)} → ${formatDate(to)}` : "Always");

const BannersTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = async () => {
    const { data } = await adminApi.get("/promotions/all");
    setItems(data.items);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    await adminApi.put(`/promotions/${p._id}`, { active: !p.active });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await adminApi.delete(`/promotions/${id}`);
    load();
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          The website shows the banners switched on, inside their dates, in order. With none on, it shows the default banner (free additional driver).
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, item: null })} sx={{ backgroundColor: "#1C2541" }}>
          New Banner
        </Button>
      </Box>
      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>On site</strong></TableCell>
              <TableCell><strong>Banner</strong></TableCell>
              <TableCell><strong>Where</strong></TableCell>
              <TableCell><strong>Dates</strong></TableCell>
              <TableCell><strong>Order</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p._id} hover sx={{ opacity: p.active ? 1 : 0.65 }}>
                <TableCell><Switch checked={p.active} onChange={() => toggle(p)} /></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[p.content?.en?.title, p.content?.en?.highlight].filter(Boolean).join(" ")}
                    {p.discountCode ? ` · code ${p.discountCode}` : ""}
                  </Typography>
                </TableCell>
                <TableCell>{(p.placements || []).map((c) => <Chip key={c} label={c} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                <TableCell>{period(p.startsAt, p.endsAt)}</TableCell>
                <TableCell>{p.order}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <IconButton size="small" onClick={() => setDialog({ open: true, item: p })}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => remove(p._id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && (
              <TableRow><TableCell colSpan={6} align="center">No banners yet. The site shows the default one.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PromotionDialog open={dialog.open} promotion={dialog.item} onClose={() => setDialog({ open: false, item: null })} onSaved={load} />
    </Box>
  );
};

const CodesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = async () => {
    const { data } = await adminApi.get("/discount-codes");
    setItems(data.items);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (c) => {
    await adminApi.put(`/discount-codes/${c._id}`, { active: !c.active });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this code? Customers will no longer be able to use it.")) return;
    await adminApi.delete(`/discount-codes/${id}`);
    load();
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          Customers type the code in the booking summary. It takes money off the car rental only.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, item: null })} sx={{ backgroundColor: "#1C2541" }}>
          New Code
        </Button>
      </Box>
      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>Active</strong></TableCell>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Discount</strong></TableCell>
              <TableCell><strong>Valid</strong></TableCell>
              <TableCell><strong>Min. days</strong></TableCell>
              <TableCell><strong>Uses</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c._id} hover sx={{ opacity: c.active ? 1 : 0.65 }}>
                <TableCell><Switch checked={c.active} onChange={() => toggle(c)} /></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" fontFamily="monospace">{c.code}</Typography>
                  {c.description && <Typography variant="caption" color="text.secondary">{c.description}</Typography>}
                </TableCell>
                <TableCell>{c.type === "percent" ? `${c.value}%` : `${c.value} €`}</TableCell>
                <TableCell>{period(c.validFrom, c.validTo)}</TableCell>
                <TableCell>{c.minDays || "—"}</TableCell>
                <TableCell>{c.maxUses ? `${c.uses} / ${c.maxUses}` : c.uses}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <IconButton size="small" onClick={() => setDialog({ open: true, item: c })}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => remove(c._id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && (
              <TableRow><TableCell colSpan={7} align="center">No discount codes yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CodeDialog open={dialog.open} code={dialog.item} onClose={() => setDialog({ open: false, item: null })} onSaved={load} />
    </Box>
  );
};

const PromotionsManager = () => {
  const [tab, setTab] = useState("banners");
  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="#1C2541" mb={1}>Promotions</Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="banners" label="Banners" />
        <Tab value="codes" label="Discount codes" />
      </Tabs>
      {tab === "banners" ? <BannersTab /> : <CodesTab />}
    </Box>
  );
};

export default PromotionsManager;
